import {
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  type SelectChangeEvent,
} from "@mui/material";
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";

const PARAM = "health_checks";

export interface HealthChecksFilterProps {
  options: string[];
  label?: string;
}

export function HealthChecksFilter({ options, label = "Health checks" }: HealthChecksFilterProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  const selected = useMemo(() => {
    const raw = searchParams.get(PARAM);
    if (!raw) return [];
    return raw
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  }, [searchParams]);

  // Display: when nothing in URL, show "All" and treat as all selected for filtering
  const value = useMemo(
    () => (selected.length === 0 ? options : selected.filter((s) => options.includes(s))),
    [selected, options],
  );

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const next = (event.target.value as string[]).filter((s) => options.includes(s));
    setSearchParams(
      (prev) => {
        const nextParams = new URLSearchParams(prev);
        if (next.length === 0 || next.length === options.length) {
          nextParams.delete(PARAM);
        } else {
          nextParams.set(PARAM, next.join(","));
        }
        return nextParams;
      },
      { replace: true },
    );
  };

  if (options.length === 0) return null;

  return (
    <FormControl size="small" sx={{ minWidth: 200 }}>
      <InputLabel id="health-checks-filter-label">{label}</InputLabel>
      <Select<string[]>
        labelId="health-checks-filter-label"
        multiple
        value={value.length === 0 ? options : value}
        label={label}
        onChange={handleChange}
        renderValue={(sel) => {
          if (sel.length === 0 || sel.length === options.length) return "All";
          return `${sel.length} selected`;
        }}
      >
        {options.map((opt) => (
          <MenuItem key={opt} value={opt}>
            <Checkbox checked={value.includes(opt)} size="small" />
            <ListItemText primary={opt} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
